/** @prettier */

/**
 * ログイン
 */

import {Grid, Box, Stack} from '@mui/material';
import {Auth} from 'aws-amplify';
import {useState, useEffect} from 'react';
import {useLocation, Navigate, useSearchParams, Link} from 'react-router-dom';

//MTH component import
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import ChangeTempPassword from './ChangeTempPassword';
import LoginFooter from '../../component/LoginFooter';
import {PrimaryButton} from '../../component/BasicButton';
//Style import
import '../../css/common/common.css';

import {useUserState} from '../Root';
import {routeList} from '../PrivateRoute';
import {format} from '../../define';

export const Login = () => {
  const location = useLocation();
  const {isAuthenticated} = useUserState();

  //クエリ文字列取得
  const [searchParams, setSearchParams] = useSearchParams();
  const emailVerify = searchParams.get('email-verify');

  //ログイン後に遷移するページ取得
  //未認証状態で深いアドレスに飛んだ場合、ログイン画面に戻されるが、ログイン後は
  //マイサイトではなく、本来行きたかったページに直接飛べるようにする
  //

  let transitionPage = routeList.mySite.path;
  if (location.state !== null && location.state.from !== null) {
    transitionPage = location.state.from;
  }
  const [emailAddr, setEmailAddr] = useState(''); //メールアドレス
  const [password, setPassword] = useState(''); //パスワード
  const [signInBtnDisabled, setSignInBtnDisabled] = useState(true); //SIGN INボタン有効フラグ
  const [changePassword, setChangePassword] = useState(false); //一時パスワード変更フラグ
  const [signInData, setSignInData] = useState(); //認証データ
  const [authResult, setAuthResult] = useState(''); //認証結果
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [emailAddrError, setEmailAddrError] = useState(false); //メールアドレス
  const [passwordError, setPasswordError] = useState(false); //パスワード

  //メールアドレス検証後のリダイレクト用のメッセージ
  useEffect(() => {
    if (emailVerify === 'true') {
      alert('Verification completed.');
    } else if (emailVerify === 'false') {
      alert('Verification failure.');
    }
    setSearchParams({});
  }, [emailVerify]);

  //SIGN INボタン有効無効判定
  useEffect(() => {
    if (
      emailAddr === '' ||
      password === '' ||
      emailAddrError ||
      passwordError ||
      apiWait
    ) {
      setSignInBtnDisabled(true);
    } else {
      setSignInBtnDisabled(false);
    }
    //メールアドレス入力、パスワード入力、API待ちのみ監視でよいためeslintのエラーを無視
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailAddr, password, apiWait]);

  /**
   * SIGN INボタン押下時処理
   * @param event
   */
  const handleSubmit = () => {
    setAPIWait(true);

    //認証
    Auth.signIn(emailAddr, password)
      .then(data => {
        setAuthResult('signIn');
        if (data.challengeName === 'NEW_PASSWORD_REQUIRED') {
          //一時パスワードで認証した場合
          //TODO:テスト保留 動作未確認
          setSignInData(data);
          setChangePassword(true);
        } else {
          //認証が完了してもrootコンポーネントのLoaderが動作せず、isAuthenticatedが更新されないため
          //画面更新を行い、rootのLoaderを実行させる。isAuthenticatedがtrueとなることで、遷移する。
          window.location.reload();
        }
      })
      .catch(err => {
        setAuthResult('signInFailure');
        //仮パスワード期限切れの場合useNavigate
        if (
          err.code === 'NotAuthorizedException' &&
          err.message ===
            'Temporary password has expired and must be reset by an administrator.'
        ) {
          setAuthResult('passwordExpired');
        }
      })
      .finally(() => {
        setAPIWait(false);
      });
  };

  return (
    <>
      {/* 認証済となったらページ移動する */}
      {isAuthenticated ? (
        <Navigate to={transitionPage} replace={true} />
      ) : (
        <Grid container className="loginMain">
          {/* ログインフォーム側（ログイン画面 or 一時パスワード変更画面） */}
          <Grid item xs={12} md={6} className="loginMain__leftFrame">
            <Stack className="loginMain__leftFrame">
              <Box className="loginMain__leftFrame__input">
                {changePassword ? (
                  <ChangeTempPassword
                    signInData={signInData}
                    emailAddr={emailAddr}
                  />
                ) : (
                  <Stack
                    spacing={2}
                    className="loginMain__leftFrame__input__contents">
                    <h1>Login</h1>
                    {/* 入力必須箇所に空欄が有る場合は、警告文を表示 */}
                    {authResult === 'signInFailure' ? (
                      <Box
                        className="loginMain__leftFrame__input__contents__errorText"
                        id="mv-errTxtLoginUserFormat">
                        The email address or password is incorrect. Please try
                        again.
                      </Box>
                    ) : authResult === 'passwordExpired' ? (
                      <Box
                        className="loginMain__leftFrame__input__contents__errorText"
                        id="mv-errTxtPasswordExpired">
                        Temporary password has expired.
                        <br />
                        Please ask the administrator to delete the temporary
                        account and send the invitation again.
                      </Box>
                    ) : (
                      <></>
                    )}
                    {/* ログイン用メールアドレス入力テキストボックス */}
                    <ChkInputTextbox
                      setValue={setEmailAddr}
                      setError={setEmailAddrError}
                      chkInputTxtError={format.BASIC_PATTERN_ERROR}
                      chkLength={format.EMAIL_LENGTH_MAX}
                      type="text"
                      label="E-mail"
                      value={emailAddr}
                      id="mv-inputLoginUser"
                      helperText=" "
                      errHelperText="Incorrect entry."
                      name="E-mail"
                      autoFocus={true}
                      onKeyDown={event => {
                        if (
                          event.key === 'Enter' &&
                          signInBtnDisabled === false
                        ) {
                          event.preventDefault();
                          handleSubmit();
                        }
                      }}
                    />
                    {/* ログイン用パスワード入力テキストボックス */}
                    <ChkInputTextbox
                      setValue={setPassword}
                      setError={setPasswordError}
                      chkLength={format.PASSWORD_LENGTH_MAX}
                      type="password"
                      label="Password"
                      value={password}
                      id="mv-inputLoginPassword"
                      helperText=" "
                      errHelperText="Incorrect entry."
                      name="Password"
                      onKeyDown={event => {
                        if (
                          event.key === 'Enter' &&
                          signInBtnDisabled === false
                        ) {
                          event.preventDefault();
                          handleSubmit();
                        }
                      }}
                    />
                    {/* サインインボタン */}
                    <Box className="loginMain__leftFrame__input__contents__button">
                      {/* 入力フォーム状態によって有効/無効が切り替わる */}
                      {/* useEffect()にてそれを判定する */}
                      <PrimaryButton
                        onClick={handleSubmit}
                        buttonText={'Sign In'}
                        id="mv-btnLogin"
                        disabled={signInBtnDisabled}
                        className="loginMain__leftFrame__input__contents__button--signIn"
                        apiWait={apiWait}
                      />
                    </Box>
                    {/* ユーザ新規作成のリンク */}
                    <Box className="loginMain__leftFrame__input__contents__description">
                      If you do not have an account
                      {' ' /** 単語のため意図時に追加 */}
                      <Link
                        to={routeList.registerAccount.path}
                        id="mv-linkRegisterAccount"
                        data-testid="linkNewAccount">
                        Click here.
                      </Link>
                    </Box>
                    {/* パスワードリセットのリンク */}
                    <Box className="loginMain__leftFrame__input__contents__description">
                      If you have forgotten your password,
                      {' ' /** 単語のため意図時に追加 */}
                      <Link
                        to={routeList.resetPassword.path}
                        id="mv-linkResetPassword"
                        data-testid="linkResetPassword">
                        Click here.
                      </Link>
                    </Box>
                  </Stack>
                )}
              </Box>
              <LoginFooter />
            </Stack>
          </Grid>
          {/* 画像表示側 */}
          <Grid
            item
            xs={0}
            md={6}
            style={{
              backgroundImage: `url(${process.env.PUBLIC_URL}/img/mysite/login/login.webp)`,
            }}
            className="loginMain__backgroundImageIn"></Grid>
        </Grid>
      )}
    </>
  );
};
export default Login;
